import { MapPinOff, SearchX, ShieldAlert, Truck } from 'lucide-react';
import type { ReactNode } from 'react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { sectionTrail } from '../../../shared/components/section-trail';
import { Skeleton } from '../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../shared/components/SkeletonRegion';
import { ConnectionBadge } from '../../../shared/components/ConnectionBadge';
import { LiveMapFilters } from '../components/LiveMapFilters';
import { TruckPanel } from '../components/TruckPanel';
import { TruckStatusLegend } from '../components/TruckStatusLegend';
import { useLiveMapPage, type LiveMapPageView } from '../hooks/use-live-map-page';

function MapSkeleton() {
  return (
    <SkeletonRegion label="Loading live map" className="h-full">
      <Skeleton className="h-full rounded-lg" />
    </SkeletonRegion>
  );
}

/** Sits over the map rather than replacing it: the map stays mounted, so it is there the moment a truck reports. */
function OverMap({ children }: { children: ReactNode }) {
  return <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-surface/90">{children}</div>;
}

function overMapNotice(page: LiveMapPageView): ReactNode {
  if (page.tileKey === undefined || page.state.status === 'loading') return <MapSkeleton />;
  if (page.state.status === 'error') {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="Couldn't load the trucks"
        description="Something went wrong loading the fleet's positions. Try reloading the page."
      />
    );
  }
  if (page.totalCount === 0) {
    return <EmptyState icon={Truck} title="No trucks have reported a position" description="Trucks appear here as soon as one reports where it is." />;
  }
  if (page.isFiltered && page.shownCount === 0) {
    return <EmptyState icon={SearchX} title="No trucks match" description="Nothing on the map fits these filters. Clear one to see more." />;
  }
  return null;
}

/** Live positions of the whole fleet: filters, the map, and the open truck's panel beside it. */
export function LiveMapPage() {
  const { containerRef, page } = useLiveMapPage();
  const notice = overMapNotice(page);

  return (
    <>
      <PageHeader
        title="Live map"
        breadcrumbs={sectionTrail('live-map')}
        description="Where the fleet is now. Positions move as they arrive."
        actions={<ConnectionBadge connection={page.connection} />}
      />
      {page.tileKey === null ? (
        <EmptyState
          icon={MapPinOff}
          title="Map key not configured"
          description="This deployment hasn't set a MapTiler key, so there is no map to put the trucks on."
        />
      ) : (
        <div className="flex flex-col gap-3">
          <LiveMapFilters filters={page.filters} onChange={page.setFilters} />
          <div className="relative h-[60vh] min-h-96">
            <div ref={containerRef} className="h-full w-full rounded-lg border border-line" />
            {notice && <OverMap>{notice}</OverMap>}
            {page.selectedTruck && (
              <div className="absolute top-3 right-3 z-20">
                <TruckPanel truck={page.selectedTruck} now={page.now} onClose={page.closeTruck} />
              </div>
            )}
          </div>
          <TruckStatusLegend />
        </div>
      )}
    </>
  );
}
